/**
 * Rate ceiling arithmetic. Pure.
 *
 * SBA does not publish a "predicted rate" and this estimator does not invent
 * one. What SBA publishes is a CEILING: the maximum a lender may charge is the
 * base rate plus a spread fixed by gross loan amount (SOP 50 10, Appendix 18).
 * So the ceiling here is a real, citable number, and the estimate is positioned
 * underneath it by relative risk.
 *
 * Variable-rate loans only. See sba-params.json → fixedRateMaximums for why
 * fixed-rate loans are out of scope rather than approximated.
 */

import params from '../data/sba-params.json';

export interface SpreadTier {
  maxAmount: number | null;
  spreadPct: number;
  label: string;
}

const TIERS = params.variableRateSpreadTiers.tiers as SpreadTier[];

export const PROGRAM_MAX_LOAN = params.programLimits.maxLoanAmount;
export const SPREAD_SOURCE = params.variableRateSpreadTiers.source;
export const SPREAD_ASOF = params.variableRateSpreadTiers.asOf;
export const SPREAD_VERIFIED = params.variableRateSpreadTiers.verified;
export const BASE_RATE_FALLBACK_PCT = params.baseRate.fallbackPct;
export const BASE_RATE_FALLBACK_ASOF = params.baseRate.fallbackAsOf;
export const BASE_RATE_SANITY: readonly [number, number] =
  params.baseRate.sanityRangePct as [number, number];

/**
 * The Appendix 18 spread tier for a gross loan amount.
 * Tiers are inclusive upper bounds: $50,000 exactly falls in the "$50,000 or
 * less" tier, $50,001 in the next. Boundary behaviour is covered by tests.
 */
export function spreadTierFor(amount: number): SpreadTier {
  for (const tier of TIERS) {
    if (tier.maxAmount === null || amount <= tier.maxAmount) return tier;
  }
  // Unreachable while the final tier has maxAmount: null, but a malformed
  // config should fail loudly rather than silently price at zero spread.
  throw new Error('No spread tier matched; sba-params.json is malformed.');
}

/** base + spread: the maximum rate SBA permits for this loan size. */
export function rateCeilingPct(amount: number, basePct: number): number {
  return round2(basePct + spreadTierFor(amount).spreadPct);
}

/**
 * Parse the Bank prime loan rate from the FRED `fredgraph.csv` response for
 * series DPRIME. Pure so it can be tested without network.
 *
 * The CSV carries a header row then `observation_date,DPRIME` rows, most recent
 * last. Missing observations appear as `.` and are skipped. Returns null rather
 * than guessing if nothing usable is found — callers fall back to the pinned
 * value and must surface that they did.
 */
export function parsePrimeCsv(csv: string): { pct: number; asOf: string } | null {
  const lines = csv.trim().split(/\r?\n/);
  for (let i = lines.length - 1; i >= 1; i--) {
    const line = lines[i];
    if (!line) continue;
    const parts = line.split(',');
    const date = parts[0]?.trim();
    const raw = parts[1]?.trim();
    if (!date || !raw || raw === '.') continue;
    const pct = Number(raw);
    if (!Number.isFinite(pct)) continue;
    if (pct < BASE_RATE_SANITY[0] || pct > BASE_RATE_SANITY[1]) continue;
    return { pct, asOf: date };
  }
  return null;
}

/**
 * Standard fully-amortising monthly payment.
 * Handles the 0% edge case, which the closed form divides by zero on.
 */
export function monthlyPayment(principal: number, annualRatePct: number, termMonths: number): number {
  if (termMonths <= 0) throw new Error('termMonths must be positive');
  if (principal <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return principal / termMonths;
  const factor = Math.pow(1 + r, termMonths);
  return (principal * r * factor) / (factor - 1);
}

export function totalInterest(principal: number, annualRatePct: number, termMonths: number): number {
  return monthlyPayment(principal, annualRatePct, termMonths) * termMonths - principal;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
